import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Search, Plus } from 'lucide-react';
import { getAvatarUrl } from '@/utils/storage';

interface Practitioner {
  id: string;
  first_name: string;
  last_name: string;
  professional_title: string;
  avatar_url: string;
  profile_status: string;
  is_verified: boolean;
  accepting_new_clients: boolean;
}

interface PractitionerSearchProps {
  onSelectPractitioner: (practitioner: Practitioner) => void;
  excludeIds?: string[];
}

export default function PractitionerSearch({ onSelectPractitioner, excludeIds = [] }: PractitionerSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchPractitioners();
    } else {
      setPractitioners([]);
    }
  }, [searchTerm]);

  const searchPractitioners = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('practitioners')
        .select('id, first_name, last_name, professional_title, avatar_url, profile_status, is_verified, accepting_new_clients')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .eq('profile_status', 'approved')
        .eq('is_verified', true)
        .not('id', 'in', `(${excludeIds.join(',')})`)
        .limit(10);

      if (error) throw error;
      setPractitioners(data || []);
    } catch (error) {
      console.error('Search error:', error);
      setPractitioners([]);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (practitioner: Practitioner) => {
    return `${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Unknown';
  };

  const getAvatarSrc = (avatarUrl: string) => {
    if (!avatarUrl) return '/placeholder.svg';
    if (avatarUrl.startsWith('http') || avatarUrl.startsWith('data:')) return avatarUrl;
    return getAvatarUrl('practitioner.photo', avatarUrl);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="搜索 practitioner 姓名..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">搜索中...</div>
      )}

      {practitioners.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {practitioners.map((practitioner) => (
            <Card key={practitioner.id} className="hover:bg-muted/50 cursor-pointer transition-colors">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getAvatarSrc(practitioner.avatar_url)} />
                      <AvatarFallback>
                        {getDisplayName(practitioner).split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{getDisplayName(practitioner)}</div>
                      <div className="text-sm text-muted-foreground">{practitioner.professional_title}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={practitioner.accepting_new_clients ? "secondary" : "outline"}>
                      {practitioner.accepting_new_clients ? '接受新客户' : '暂不接受'}
                    </Badge>
                    <Button 
                      size="sm" 
                      onClick={() => onSelectPractitioner(practitioner)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      添加
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {searchTerm.length >= 2 && practitioners.length === 0 && !loading && (
        <div className="text-sm text-muted-foreground">未找到匹配的 practitioners</div>
      )}
    </div>
  );
}